import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuth } from "../../contexts/AuthContext";
import { businessApi, stripeApi } from "../../services/api";

/** Same business id as other business APIs: from profile PK, not auth user.PK (USER#… vs BUSINESS#…). */
const businessIdFromProfile = (profile) => {
  if (!profile) return null;
  let raw = profile.PK ?? profile.business_id;
  if (raw == null || raw === "") return null;
  let id = String(raw);
  if (id.startsWith("BUSINESS#")) id = id.slice("BUSINESS#".length);
  return id || null;
};

const UpdatePayment = () => {
  const navigate = useNavigate();
  const { tokens } = useAuth();

  const accessToken = tokens?.access_token;
  // Effects below depend on this boolean, never on the token value itself.
  // authClient swaps the stored token underneath us when it refreshes; keying
  // effects off the string would re-run them and duplicate these requests.
  const hasSession = !!accessToken;

  const [businessId, setBusinessId] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);

  const [loadingStatus, setLoadingStatus] = useState(true);
  const [startingStripe, setStartingStripe] = useState(false);
  const [payoutStatus, setPayoutStatus] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const loadProfile = async () => {
      if (!hasSession) {
        setProfileLoading(false);
        setBusinessId(null);
        return;
      }

      setProfileLoading(true);
      try {
        const profile = await businessApi.getMyProfile();
        if (cancelled) return;
        const id = businessIdFromProfile(profile);
        setBusinessId(id);
        if (!id) {
          toast.error(
            "Your business profile is missing an ID. Please complete your profile first.",
            { position: "top-right", autoClose: 5000 }
          );
        }
      } catch (err) {
        if (cancelled) return;
        setBusinessId(null);
        // A 401 here means the token was expired AND the refresh failed, so
        // authClient has already cleared the session and AuthContext is
        // redirecting to the sign-in page. Showing the server's raw wording on
        // the way out is how "Invalid bearer for this route (ACCESS token
        // required): Token has expired" ended up in front of a partner.
        if (err?.isAuthError) return;
        const msg =
          (typeof err?.error === "string" && err.error) ||
          err?.message ||
          "Failed to load business profile";
        toast.error(msg, { position: "top-right", autoClose: 5000 });
      } finally {
        if (!cancelled) setProfileLoading(false);
      }
    };

    loadProfile();
    return () => {
      cancelled = true;
    };
  }, [hasSession]);

  const syncFromStripe = useCallback(async () => {
    if (profileLoading || !hasSession || !businessId) {
      if (!profileLoading && (!hasSession || !businessId)) {
        setLoadingStatus(false);
      }
      return;
    }

    setLoadingStatus(true);
    try {
      const data = await stripeApi.completeVendorOnboarding({
        business_id: businessId,
      });
      const newVendorId = data.stripeVendorId ?? data.stripe_vendor_id ?? null;
      setPayoutStatus({
        stripeVendorId: newVendorId,
        payoutsEligible: Boolean(
          data.payoutsEligible ?? data.payoutEligible ?? data.payouts_enabled
        ),
        detailsSubmitted: Boolean(
          data.detailsSubmitted ?? data.details_submitted
        ),
      });
      if (newVendorId) {
        try {
          await businessApi.updateProfile({
            business_id: businessId,
            stripeVendorId: newVendorId,
          });
        } catch (_) {
          // Non-fatal
        }
      }
      const params = new URLSearchParams(window.location.search);
      if (params.get("from_stripe") === "1") {
        toast.success("Payout settings synced from Stripe.", {
          position: "top-right",
          autoClose: 4000,
        });
        navigate("/admin-dashboard/update-payment", { replace: true });
      } else if (params.get("stripe_refresh") === "1") {
        toast.info(
          "Your Stripe session may have expired. Use the button below to continue setup.",
          { position: "top-right", autoClose: 5000 }
        );
        navigate("/admin-dashboard/update-payment", { replace: true });
      }
    } catch (err) {
      setPayoutStatus(null);
      // Was /unauthorized|invalid token/i, which never matched the message the
      // backend actually sends for a dead session ("Token has expired") — so an
      // expired token fell to the else branch and the partner was told to "try
      // again in a moment" instead of to sign in. Branch on the status now.
      // authClient handles the sign-out and redirect itself.
      if (err?.isAuthError) return;

      const params = new URLSearchParams(window.location.search);
      if (params.get("from_stripe") === "1") {
        toast.warning(
          "Could not sync payout status yet. Try again in a moment or contact support if this continues.",
          { position: "top-right", autoClose: 6000 }
        );
        navigate("/admin-dashboard/update-payment", { replace: true });
      }
    } finally {
      setLoadingStatus(false);
    }
  }, [hasSession, businessId, navigate, profileLoading]);

  useEffect(() => {
    syncFromStripe();
  }, [syncFromStripe]);

  const handleContinueToStripe = async () => {
    if (!hasSession) {
      toast.error("Authentication expired. Please log in again.", {
        position: "top-right",
        autoClose: 4000,
      });
      return;
    }
    if (!businessId) {
      toast.error("Unable to get business ID. Please log in again.", {
        position: "top-right",
        autoClose: 4000,
      });
      return;
    }

    const origin = window.location.origin;
    const returnUrl = `${origin}/admin-dashboard/update-payment?from_stripe=1`;
    const refreshUrl = `${origin}/admin-dashboard/update-payment?stripe_refresh=1`;

    setStartingStripe(true);
    try {
      const data = await stripeApi.onboardVendor({
        business_id: businessId,
        return_url: returnUrl,
        refresh_url: refreshUrl,
      });
      const url = data?.onboardingUrl || data?.onboarding_url;
      if (!url || typeof url !== "string") {
        toast.error("Stripe did not return an onboarding link.", {
          position: "top-right",
          autoClose: 4000,
        });
        return;
      }
      window.location.assign(url);
    } catch (err) {
      if (err?.isAuthError) return; // authClient is already signing them out
      const msg =
        (typeof err?.error === "string" && err.error) ||
        err?.message ||
        "Failed to create onboarding link";
      toast.error(msg, { position: "top-right", autoClose: 5000 });
    } finally {
      setStartingStripe(false);
    }
  };

  const handleCancel = () => {
    navigate("/admin-dashboard");
  };

  if (!hasSession) {
    return (
      <p style={{ color: "#555", fontSize: "16px", textAlign: "left" }}>
        Please sign in to manage payout settings.
      </p>
    );
  }

  if (profileLoading) {
    return (
      <p style={{ color: "#555", fontSize: "16px", textAlign: "left" }}>
        Loading your business profile…
      </p>
    );
  }

  if (!businessId) {
    return (
      <p style={{ color: "#555", fontSize: "16px", textAlign: "left" }}>
        Unable to determine your business account from your profile. Please
        complete your business profile or try again later.
      </p>
    );
  }

  return (
    <>
      <h2
        style={{
          textAlign: "left",
          marginBottom: "15px",
          fontSize: "clamp(24px, 6vw, 40px)",
          fontWeight: "bold",
          color: "#303030",
        }}
      >
        Update payment
      </h2>
      <p
        style={{
          marginBottom: "24px",
          color: "#555",
          fontSize: "16px",
          textAlign: "left",
          lineHeight: 1.5,
        }}
      >
        Bank and tax details are entered only on Stripe&apos;s secure
        onboarding page. We do not collect or store account or routing numbers
        in this app.
      </p>

      <div
        style={{
          marginBottom: "32px",
          padding: "20px",
          borderRadius: "12px",
          border: "1px solid #e0e0e0",
          background: "#fafafa",
        }}
      >
        <h3
          style={{
            margin: "0 0 12px",
            fontSize: "18px",
            fontWeight: "bold",
            color: "#303030",
          }}
        >
          Payout status
        </h3>
        {loadingStatus ? (
          <p style={{ margin: 0, color: "#666", fontSize: "15px" }}>
            Syncing payout status…
          </p>
        ) : payoutStatus ? (
          <ul
            style={{
              margin: 0,
              paddingLeft: "20px",
              color: "#444",
              fontSize: "15px",
              lineHeight: 1.7,
            }}
          >
            <li>
              Details submitted:{" "}
              <strong>
                {payoutStatus.detailsSubmitted ? "Yes" : "No"}
              </strong>
            </li>
            <li>
              Payouts enabled:{" "}
              <strong>
                {payoutStatus.payoutsEligible ? "Yes" : "No"}
              </strong>
            </li>
            {payoutStatus.stripeVendorId ? (
              <li style={{ wordBreak: "break-all" }}>
                Stripe account:{" "}
                <span style={{ fontFamily: "monospace", fontSize: "13px" }}>
                  {payoutStatus.stripeVendorId}
                </span>
              </li>
            ) : null}
          </ul>
        ) : (
          <p style={{ margin: 0, color: "#666", fontSize: "15px" }}>
            No Stripe Connect account is linked yet, or details are still
            pending. Use the button below to open Stripe and complete setup.
          </p>
        )}
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: "20px", marginBottom: "20px" }}>
        <button
          type="button"
          disabled={startingStripe || !hasSession || !businessId}
          onClick={handleContinueToStripe}
          style={{
            width: "auto",
            minWidth: "200px",
            height: "50px",
            padding: "0 24px",
            borderRadius: "30px",
            border: "none",
            cursor:
              startingStripe || !hasSession || !businessId
                ? "not-allowed"
                : "pointer",
            background: "#DE810E",
            color: "white",
            fontWeight: "bold",
            fontSize: "16px",
            opacity: startingStripe || !hasSession || !businessId ? 0.7 : 1,
          }}
        >
          {startingStripe
            ? "Opening Stripe…"
            : payoutStatus?.detailsSubmitted
              ? "Update in Stripe"
              : "Set up payouts with Stripe"}
        </button>
        <button
          type="button"
          onClick={handleCancel}
          style={{
            width: "140px",
            height: "50px",
            borderRadius: "30px",
            border: "2px solid #DE810E",
            cursor: "pointer",
            background: "white",
            color: "#DE810E",
            fontWeight: "bold",
            fontSize: "16px",
          }}
        >
          Cancel
        </button>
      </div>

      <p
        style={{
          fontSize: "14px",
          color: "#777",
          margin: 0,
          textAlign: "left",
        }}
      >
        Powered by Stripe
      </p>
    </>
  );
};

export default UpdatePayment;
